/* MAG Signature Sound — Track Insight customer auth
   v2 — same-tab Shopify OAuth hand-off
*/
(function () {
  'use strict';

  const PROJECT_REF = 'jbllyryqhalyltkkkljn';
  const FUNCTIONS_BASE = `https://${PROJECT_REF}.supabase.co/functions/v1`;
  const STORAGE_KEY = 'mag_track_insight_customer_auth';
  const REFRESH_SKEW_MS = 60000;

  let bootstrapError = null;

  function loadSession() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (_) {
      return null;
    }
  }

  function saveSession(payload) {
    const session = {
      shopify_customer_id: payload.shopify_customer_id || null,
      email: payload.email || null,
      access_token: payload.access_token || null,
      id_token: payload.id_token || null,
      refresh_token: payload.refresh_token || null,
      expires_at: Date.now() + (Number(payload.expires_in || 0) * 1000)
    };

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    return session;
  }

  function clearSession() {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  async function parseJson(res) {
    let body = {};

    try {
      body = await res.json();
    } catch (_) {}

    if (!res.ok) {
      throw new Error(body.error || `Request failed (${res.status})`);
    }

    return body;
  }

  function cleanAuthParams() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('ti_auth_code');
      url.searchParams.delete('ti_auth_error');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } catch (_) {}
  }

  async function consumeAuthResultIfNeeded() {
    const params = new URLSearchParams(window.location.search);
    const resultCode = params.get('ti_auth_code');
    const authError = params.get('ti_auth_error');

    if (!resultCode && !authError) {
      return false;
    }

    if (authError) {
      cleanAuthParams();
      bootstrapError = new Error(authError);
      return true;
    }

    try {
      const res = await fetch(
        `${FUNCTIONS_BASE}/customer-oauth-callback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            result_code: resultCode
          })
        }
      );

      const data = await parseJson(res);
      saveSession(data);
      cleanAuthParams();
      return true;
    } catch (error) {
      cleanAuthParams();
      bootstrapError =
        error instanceof Error
          ? error
          : new Error(String(error));
      return true;
    }
  }

  const authReady = consumeAuthResultIfNeeded();

  async function refreshSession(session) {
    if (!session || !session.refresh_token) {
      clearSession();
      throw new Error(
        'Your Track Insight sign-in has expired. Please sign in again.'
      );
    }

    const res = await fetch(
      `${FUNCTIONS_BASE}/customer-oauth-refresh`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: session.refresh_token
        })
      }
    );

    const data = await parseJson(res);

    return saveSession({
      ...data,
      email: data.email || session.email || null,
      refresh_token: data.refresh_token || session.refresh_token
    });
  }

  async function getSession() {
    await authReady;

    if (bootstrapError) {
      throw bootstrapError;
    }

    let session = loadSession();

    if (!session || !session.access_token) {
      return null;
    }

    if (
      !session.expires_at ||
      session.expires_at <= Date.now() + REFRESH_SKEW_MS
    ) {
      session = await refreshSession(session);
    }

    return session;
  }

  async function requireSession() {
    const session = await getSession();

    if (!session) {
      throw new Error('Please sign in to Track Insight first.');
    }

    return session;
  }

  async function api(action, params) {
    let session = await requireSession();

    async function request(currentSession) {
      return fetch(
        `${FUNCTIONS_BASE}/submissions-api`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentSession.access_token}`
          },
          body: JSON.stringify({
            action,
            ...(params || {})
          })
        }
      );
    }

    let res = await request(session);

    if (res.status === 401 && session.refresh_token) {
      session = await refreshSession(session);
      res = await request(session);
    }

    return parseJson(res);
  }

  async function signIn() {
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.delete('ti_auth_code');
    returnUrl.searchParams.delete('ti_auth_error');

    const res = await fetch(
      `${FUNCTIONS_BASE}/customer-oauth-start`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          return_url: returnUrl.toString()
        })
      }
    );

    const data = await parseJson(res);

    if (!data.authorize_url) {
      throw new Error(
        'No Shopify authorization URL was returned.'
      );
    }

    /*
      Use the current tab instead of a popup.
      Locksmith has already authenticated the Shopify customer, so this
      Customer Account API authorization hand-off should return straight
      to the same Track Insight page without asking for another login.
    */
    window.location.assign(data.authorize_url);

    /*
      Keep callers awaiting while the page navigates away.
    */
    return new Promise(function () {});
  }

  async function listSubmissions() {
    const data = await api('list_submissions');
    return data.submissions || [];
  }

  async function findAwaitingUpload() {
    const submissions = await listSubmissions();

    return (
      submissions.find(
        item =>
          item.status ===
          'Awaiting Upload'
      ) || null
    );
  }

  window.TrackInsightCustomer = {
    projectRef: PROJECT_REF,
    functionsBase: FUNCTIONS_BASE,
    signIn,
    getSession,
    requireSession,
    clearSession,
    api,
    listSubmissions,
    findAwaitingUpload,
    authReady
  };

  authReady.finally(function () {
    window.dispatchEvent(
      new CustomEvent(
        'track-insight-customer-ready'
      )
    );
  });
})();
